
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import {
  QuoteSubscriptionRequest,
  SubscriptionInfo,
  subscriptionType,
} from "functions/src/Subscriptions/SubscriptionsInterfaces";
import {
  QuoteRequest,
  QuoteRequestType,
} from "functions/src/Payments/PaymentsInterfaces";
import { UserSubscription } from "functions/src/Users/UsersInterfaces";
import {
  normalizePackageLabel,
  PKG_FLY,
  PKG_GO,
  PKG_WIN,
  SUB_ACADEMY_NEW,
  SUB_LITE_NEW,
} from "functions/src/Subscriptions/packageIds";
import { BENEFICIOS_FLY, PackageBenefits } from "./packageBenefits";
import PackageIcon from "../../common/PackageIcon.vue";

export default defineComponent({
  name: "gtr-basic",
  components: { PackageIcon },
  data() {
    return {
      QuoteRequest: {} as QuoteRequest<QuoteRequestType>,
    };
  },
  props: {
    subscriptionName: { type: String, required: false },
    subscriptionId: { type: Object as () => subscriptionType, required: true },
    promotionId: { type: String, required: false },
    promotionType: {
      type: Object as () => "upgrade" | "promotion",
      required: false,
    },
    lastQuote: { type: Object, required: true },
    isQuoting: { type: Boolean, required: true },
  },
  emits: ["clearQuote", "requestQuote"],
  computed: {
    ...mapGetters("auth", ["getUsernamePK", "getUseremail"]),
    ...mapGetters("subs", [
      "getSubscriptionConfig",
      "durationUnitLocalized",
      "getUserSubscription",
      "getSubscriptionsUpgrade",
      "getUserCurrentPackage",
    ]),
    ...mapGetters("sys", ["isOffline"]),
    // Esta card vende GTR FLY (el upgrade desde Go), así que la copy es la de
    // Fly y sale del mismo módulo que `GtrFly.vue`. Es computed y no `data()`
    // para que Vue no envuelva en un proxy reactivo una constante que nadie muta.
    Benefits(): PackageBenefits {
      return BENEFICIOS_FLY;
    },
    isLoaded(): boolean {
      const loaded =
        this.SubscriptionConfig != undefined &&
        (this.promotionId === undefined || this.PromotionConfig != undefined);
      return loaded;
    },
    SubscriptionConfig(): SubscriptionInfo {
      return this.getSubscriptionConfig(this.subscriptionId);
    },
    PromotionConfig(): SubscriptionInfo {
      return this.getSubscriptionsUpgrade(this.promotionId);
    },
    CanUpgrade(): boolean {
      const upgradeDays = this.PromotionConfig?.upgradeWindowDays
        ? this.PromotionConfig.upgradeWindowDays
        : 0;
      const daysLeft = this.UserStatus.daysLeft || 0;
      // FALLA CERRADO: con `?.`, y si el documento del catálogo no está cargado
      // (o se ha retirado) NO se ofrece el upgrade. Sin el `?.` la tarjeta
      // reventaba en render; con un default inventado ofrecería una venta
      // calculada sobre un número que nadie escribió.
      const duration = this.getSubscriptionConfig(
        this.CurrentPackageCatalogId
      )?.duration;
      if (duration == undefined) return false;
      return duration - upgradeDays <= daysLeft;
    },
    SubscriptionDurationUnitLocalized(): string {
      return this.durationUnitLocalized(
        this.SubscriptionConfig?.duration,
        this.SubscriptionConfig?.durationUnit
      );
    },
    CurrentUserSubscription(): subscriptionType {
      // El doc-id que hay que consultar para el paquete actual. Devuelve el id
      // VIEJO a propósito: `getUserSubscription` ya resuelve la familia entera,
      // así que con esto basta para encontrar también un `gtr-fly`/`gtr-go`, y
      // no se cambia lo que la tarjeta VENDE (eso es una decisión comercial,
      // no un efecto colateral de este cambio).
      switch (normalizePackageLabel(this.getUserCurrentPackage)) {
        case PKG_GO:
          return "gtr-lite";
        case PKG_FLY:
        case PKG_WIN:
        default:
          return "trading-academy";
      }
    },
    CurrentPackageCatalogId(): subscriptionType {
      // EL MISMO PAQUETE, PERO PARA PREGUNTARLE AL CATÁLOGO — y por eso no vale
      // el de arriba.
      //
      // Son dos preguntas distintas hechas con el mismo nombre. Al USUARIO se le
      // pregunta cuántos días le quedan, y ahí el id viejo sirve porque
      // `getUserSubscription` busca la familia entera. Al CATÁLOGO se le
      // pregunta cuánto dura un ciclo, y eso es un documento suelto: el que se
      // pida es el que responde, sin familia que valga.
      //
      // Hoy los dos documentos dicen lo mismo porque el sembrador copió la
      // duración del gemelo viejo, así que esto no cambia ni una pantalla. Deja
      // de decirlo el día que alguien edite sólo el documento nuevo — que es
      // exactamente lo que va a pasar cuando lleguen los precios definitivos: la
      // ventana de upgrade se calcularía con la duración de un paquete que ya no
      // se vende, y el botón aparecería (o no) cuando no toca.
      //
      // Es además la MISMA decisión que toma la ficha de un usuario en la
      // herramienta de búsqueda, y allí ya se pide el documento nuevo. Dos
      // sitios que deciden lo mismo tienen que mirar el mismo documento.
      switch (normalizePackageLabel(this.getUserCurrentPackage)) {
        case PKG_GO:
          return SUB_LITE_NEW;
        case PKG_FLY:
        case PKG_WIN:
        default:
          return SUB_ACADEMY_NEW;
      }
    },
    UserStatus(): UserSubscription {
      return this.getUserSubscription(this.CurrentUserSubscription);
    },
    UserBasicSubscription(): UserSubscription {
      return this.getUserSubscription("trading-academy");
    },
    UserBasicDistributionLicense(): UserSubscription {
      return this.getUserSubscription("distributor-license");
    },
    DaysLeft(): number {
      return this.UserStatus.daysLeft || 0;
    },
    LicenseDays(): number {
      if (this.UserBasicSubscription.isFirstTime) {
        return this.SubscriptionConfig?.initialLicenseDays || 0;
      }
      return this.UserBasicDistributionLicense.daysLeft &&
        this.UserBasicDistributionLicense.daysLeft > 0
        ? this.UserBasicDistributionLicense.daysLeft
        : 0;
    },
  },
  methods: {
    subscriptionQuote() {
      const fiveMinutes = new Date();
      fiveMinutes.setTime(fiveMinutes.getTime() + 5 * 60 * 1000); //5 minutes from now
      if (
        !(
          this.lastQuote.subscriptionId == this.subscriptionId &&
          this.lastQuote.username == this.getUsernamePK &&
          this.lastQuote.expirationTime > fiveMinutes
        )
      ) {
        this.$emit("clearQuote");
        const request: QuoteSubscriptionRequest = {
          subscriptionId: this.subscriptionId,
          promotionId: this.CanUpgrade ? this.promotionId : undefined,
          promotionType: this.CanUpgrade ? this.promotionType : undefined,
          duration:
            this.promotionId && this.CanUpgrade
              ? this.DaysLeft + 1 // DaysLeft are rounded down, so we add 1 to get the correct value
              : this.SubscriptionConfig.duration,
          durationUnit:
            this.promotionId && this.CanUpgrade
              ? this.PromotionConfig.durationUnit
              : this.SubscriptionConfig.durationUnit,
          username: this.getUsernamePK,
          email: this.getUseremail,
          isDirectAffiliate: false,
        };
        this.$emit("requestQuote", request);
      }
    },
  },
});
